/**
 * PitchPrint Card Variants - ORIGINAL WORKING VERSION
 * Reverted to stable version without theme compatibility issues
 * Removes problematic theme integration that causes fetchAvailability errors
 */
(() => {
  'use strict';

  let initialized = false;
  const stateManager = window.PitchPrintStateManager;
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[CardVariants]', ...args);

  if (!stateManager) {
    console.error('[CardVariants] StateManager not found - using fallback mode');
  }

  // =====================================
  // SAFE VARIANT FUNCTIONS - NO THEME CONFLICTS
  // =====================================
  
  const updateCardVisuals = (optionName, selectedValue) => {
    const group = document.querySelector(`[data-option-name="${optionName}"] .variants-cards`);
    if (!group) return;
    
    // Remove active from all cards in this group
    group.querySelectorAll('.card-p-style').forEach(card => {
      card.classList.remove('active');
      if (card.dataset.value === selectedValue) {
        card.classList.add('active');
      }
    });
  };
  
  const updateHiddenSelect = (optionName, selectedValue) => {
    const select = document.querySelector(`select[name="options[${optionName}]"]`);
    if (select && select.value !== selectedValue) {
      select.value = selectedValue;
      
      // Dispatch change event safely without triggering theme conflicts
      try {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        log('⚠️ Select change event error (non-critical):', error.message);
        // Continue execution even if theme has issues
      }
    }
  };
  
  const updateVariantInput = (variantId) => {
    const input = document.querySelector('input[name="id"]');
    if (input && input.value !== variantId) {
      input.value = variantId;
      
      // Safely dispatch events
      try {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        log('⚠️ Input event error (non-critical):', error.message);
      }
    }
  };
  
  const updateURL = (variantId) => {
    try {
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variantId);
        history.replaceState(null, '', url);
      }
    } catch (error) {
      log('⚠️ URL update error (non-critical):', error.message);
    }
  };

  // =====================================
  // SAFE VARIANT APPLICATION - NO THEME CONFLICTS
  // =====================================
  
  const applyVariant = (variantId, optionsArray) => {
    // If no variantId but we have options, try to find the matching variant
    if (!variantId && optionsArray && optionsArray.length > 0) {
      log('🔍 No variant ID provided, searching for variant matching options:', optionsArray);

      // Get all selects and their current values
      const selects = document.querySelectorAll('select[name^="options"]');

      // Apply the options to the selects
      selects.forEach((select, idx) => {
        if (optionsArray[idx]) {
          select.value = optionsArray[idx];
        }
      });

      // Trigger change on the last select to update the variant ID
      if (selects.length > 0) {
        const lastSelect = selects[selects.length - 1];
        lastSelect.dispatchEvent(new Event('change', { bubbles: true }));

        // Give the variant change event time to process
        setTimeout(() => {
          // Now get the current variant ID from the form
          const variantInput = document.querySelector('input[name="id"]');
          const foundVariantId = variantInput ? variantInput.value : null;

          if (foundVariantId) {
            log('✅ Found variant ID from options:', foundVariantId);
            // Update visuals for all options
            optionsArray.forEach((value, idx) => {
              const optionNameMatch = selects[idx].name.match(/\[(.*?)\]/);
              const optionName = optionNameMatch ? optionNameMatch[1] : null;
              if (optionName) {
                updateCardVisuals(optionName, value);
                log('🎨 Updated card visuals for:', optionName, '→', value);
              }
            });
          } else {
            log('⚠️ Could not find variant ID matching options');
          }
        }, 100);
      }

      return;
    }

    if (!variantId) {
      log('⚠️ No variant ID or options provided to applyVariant');
      return;
    }

    log('🔄 Applying variant:', variantId, 'with options:', optionsArray);

    try {
      // Update all hidden selects with the option values
      if (optionsArray) {
        document.querySelectorAll('select[name^="options"]').forEach((select, idx) => {
          const value = optionsArray[idx];
          if (value) {
            // Always update select value
            const valueChanged = select.value !== value;
            select.value = value;

            // ALWAYS update card visuals, even if select value didn't change
            // This ensures visual sync when URL parameter sets the variant
            const optionNameMatch = select.name.match(/\[(.*?)\]/);
            const optionName = optionNameMatch ? optionNameMatch[1] : null;
            if (optionName) {
              updateCardVisuals(optionName, value);
              log('🎨 Updated card visuals for:', optionName, '→', value);
            }

            // Only dispatch change event if value actually changed
            if (valueChanged) {
              try {
                select.dispatchEvent(new Event('change', { bubbles: true }));
              } catch (e) {
                // Continue if theme has compatibility issues
                log('⚠️ Theme compatibility issue (non-critical):', e.message);
              }
            }
          }
        });
      }

      // Update variant ID and URL
      updateVariantInput(variantId);
      updateURL(variantId);

      log('✅ Variant applied successfully:', variantId);

    } catch (error) {
      log('❌ Error applying variant:', error);
    }
  };

  // =====================================
  // POPULAR VARIANTS - Use metafield data
  // =====================================

  const applyPopularVariants = () => {
    log('🎯 Applying popular variants from metafield');
    
    try {
      // Find the popular variant element injected by liquid template
      const popularElement = document.querySelector('[data-popular-variant]');
      if (popularElement) {
        const popularVariantId = popularElement.dataset.popularVariant;
        const popularOptionsJson = popularElement.dataset.popularOptions;
        
        if (popularVariantId && popularOptionsJson) {
          try {
            const popularOptions = JSON.parse(popularOptionsJson);
            log('📋 Popular variant data from metafield:', { id: popularVariantId, options: popularOptions });
            applyVariant(popularVariantId, popularOptions);
            return;
          } catch (error) {
            log('❌ Error parsing popular options:', error);
          }
        }
      }
      
      // Fallback: use first active card's variant
      const firstActiveCard = document.querySelector('.card-p-style.active[data-variant-id]');
      if (firstActiveCard) {
        const variantId = firstActiveCard.dataset.variantId;
        updateVariantInput(variantId);
        updateURL(variantId);
        log('✅ Applied fallback active variant:', variantId);
      }
      
    } catch (error) {
      log('❌ Error applying popular variants:', error);
    }
  };

  // =====================================
  // SAFE CLICK HANDLER - NO THEME CONFLICTS
  // =====================================
  
  const setupVariantControls = () => {
    const container = document.querySelector('.custom-tab-body');
    if (!container) {
      log('❌ Custom tab body not found');
      return;
    }

    container.addEventListener('click', (e) => {
      const card = e.target.closest('.card-p-style');
      if (!card) return;

      // Check if card is conditionally hidden
      if (card.classList.contains('conditionally-hidden')) {
        log('🙈 Click ignored - card is conditionally hidden');
        return;
      }

      // Check if controls are locked by PitchPrint
      if (card.style.pointerEvents === 'none') {
        log('🔒 Click ignored - variant controls are locked');
        return;
      }

      // Check if card is already active
      if (card.classList.contains('active')) {
        log('ℹ️ Card already active - ignoring click:', card.dataset.value);
        return;
      }

      e.preventDefault();
      
      const optionName = card.dataset.optionName;
      const selectedValue = card.dataset.value;

      log('🎯 Card clicked:', { optionName, selectedValue });

      try {
        // 1. Update card visuals
        updateCardVisuals(optionName, selectedValue);
        
        // 2. Update hidden select safely
        updateHiddenSelect(optionName, selectedValue);
        
        // 3. Wait for Shopify's variant calculation, then update URL
        setTimeout(() => {
          const calculatedVariantId = document.querySelector('input[name="id"]')?.value;
          if (calculatedVariantId) {
            updateURL(calculatedVariantId);
            log('✅ Variant calculated by Shopify:', calculatedVariantId);
          }
        }, 100);

        // 4. Update PitchPrint observables safely
        updatePitchPrintObservables();

        // 5. Only save variant state if there's an active project
        if (stateManager && stateManager.getProjectId()) {
          log('ℹ️ Variant change with active project - will update state');
        } else {
          log('ℹ️ Variant change without project - no state saving needed');
        }
        
      } catch (error) {
        log('❌ Error handling card click:', error);
      }
    });

    log('🎯 Safe variant click handlers attached');
  };

  // =====================================
  // PROJECT RESTORATION - Simplified & Safe
  // =====================================

  const handleProjectRestoration = () => {
    log('🎨 Checking for project restoration...');

    try {
      // Check if there's an active PitchPrint project
      const activeProjectId = getActiveProjectId();

      // NEW: Also check for pp_project URL parameter (My Designs duplication flow)
      const urlParams = new URLSearchParams(window.location.search);
      const urlProjectId = urlParams.get('pp_project');

      if (activeProjectId || urlProjectId) {
        const projectId = activeProjectId || urlProjectId;
        log('🔄 Found project for restoration:', projectId, urlProjectId ? '(from URL parameter)' : '(from StateManager/ppclient)');

        // Priority-based variant restoration with fallback chain
        let variantRestored = false;

        // PRIORITY 1: Check PitchPrint project storage (shopifyVariant field) - HIGHEST PRIORITY
        // This is the most reliable source as it's stored WITH the project
        const pitchprintVariantData = getProjectDataFromPitchPrintStorage(projectId);
        if (pitchprintVariantData && (pitchprintVariantData.variantId || pitchprintVariantData.options)) {
          log('💾 Found variant in PitchPrint storage (PRIORITY 1):', pitchprintVariantData);

          // If we have options but no variantId, applyVariant will find the matching variant
          // If we have variantId but no options, it will use the variantId directly
          applyVariant(pitchprintVariantData.variantId, pitchprintVariantData.options);
          variantRestored = true;
          lockControls();
        }

        // PRIORITY 2: Check localStorage pp_variant_${productId} (existing system)
        if (!variantRestored) {
          const savedVariantData = getSavedVariantData();
          if (savedVariantData && savedVariantData.projectId === projectId) {
            log('📂 Found variant in localStorage (PRIORITY 2):', savedVariantData);
            applyVariant(savedVariantData.variantId, savedVariantData.options);
            variantRestored = true;
            lockControls();
          }
        }

        // PRIORITY 3: Check URL parameters (?variant=X) - FALLBACK ONLY
        // URL variant is just what page we're on, not necessarily the correct variant for this project
        if (!variantRestored) {
          const urlParams = new URLSearchParams(window.location.search);
          const urlVariantId = urlParams.get('variant');

          if (urlVariantId) {
            log('🔗 Found variant in URL parameter (PRIORITY 3 - fallback):', urlVariantId);

            // Get the options for this variant by reading current select values
            const currentOptions = Array.from(document.querySelectorAll('select[name^="options"]'))
              .map(select => select.value)
              .filter(value => value && value !== '');

            // Apply variant with options to ensure visual cards update
            applyVariant(urlVariantId, currentOptions);
            variantRestored = true;

            // Give PitchPrint time to initialize before locking
            setTimeout(() => {
              lockControls();
              log('🔒 Locked controls after URL variant restoration');
            }, 1000);
          }
        }

        // FALLBACK: If no variant data found, lock current state
        if (!variantRestored) {
          log('⚠️ No variant data found - locking current state');
          lockControls();
        }

        // Ensure PitchPrint is in active state
        ensurePitchPrintActiveState(projectId);

        // Notify StateManager with COMPLETE project metadata
        if (stateManager) {
          const projectData = getProjectDataFromPitchPrintStorage(projectId);
          const currentVariant = getCurrentVariantData();

          // Extract metadata from project and variant
          const numPages = projectData?.numPages || projectData?.pageLength || (currentVariant.options[0] === 'Single Sided' ? 1 : 2);
          const shape = currentVariant.options[1] || null; // "Circle Shape" or "Classic Shape"
          const sides = currentVariant.options[0] || null; // "Single Sided" or "Double Sided"
          const size = currentVariant.options[2] || null; // Size option

          stateManager.setProject({
            projectId: projectId,
            variant: currentVariant,
            numPages: numPages,
            shape: shape,
            sides: sides,
            size: size
          });

          log('📡 StateManager notified with complete project data:', {
            projectId,
            variantId: currentVariant.id,
            numPages,
            shape,
            sides
          });
        }
      } else {
        log('🎯 No active project - applying popular variants');
        applyPopularVariants();
        unlockControls();
      }

    } catch (error) {
      log('❌ Error during project restoration:', error);
      // Continue with popular variants as fallback
      applyPopularVariants();
      unlockControls();
    }
  };
  // =====================================
  // PITCHPRINT INTEGRATION - Safe
  // =====================================
  
  const handleProjectSaved = async (data) => {
    log('🎉 Project saved:', data);

    try {
      const projectId = data.data?.projectId || data.projectId;
      const numPages = data.data?.numPages || data.numPages;
      const autoSaved = data.data?.autoSaved || data.autoSaved;

      if (!projectId) {
        log('❌ No project ID in save event');
        return;
      }

      log('📊 Save event data:', { projectId, numPages, autoSaved });

      const currentVariantData = getCurrentVariantData();

      // Extract complete metadata
      const eventNumPages = data.data?.numPages || data.numPages || (currentVariantData.options[0] === 'Single Sided' ? 1 : 2);
      const shape = currentVariantData.options[1] || null;
      const sides = currentVariantData.options[0] || null;
      const size = currentVariantData.options[2] || null;

      // NEW: Infer sides from numPages if available
      if (numPages) {
        const inferredSides = numPages === 1 ? 'Single Sided' : numPages === 2 ? 'Double Sided' : null;
        log('🔍 Inferred from numPages:', { numPages, inferredSides });
      }

      // Prepare payload for cloud API
      const payload = {
        projectId: projectId,
        customerId: window.ppCustomerData?.id || 'guest',
        productId: window.ppProduct?.id || getProductId(),
        variantId: currentVariantData.id,
        options: currentVariantData.options,
        numPages: eventNumPages,
        shape: shape,
        sides: sides,
        size: size,
        designId: data.designId || null,
        designTitle: data.designTitle || null,
        metadata: {
          autoSaved: autoSaved || false,
          source: 'project-saved-event',
          userAgent: navigator.userAgent,
          timestamp: Date.now()
        }
      };

      // 🆕 NEW: Call cloud persistence API
      try {
        const response = await fetch('https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging/api/projects', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shop-Domain': window.Shopify?.shop || 'studio.popsigns.co'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const result = await response.json();
          log('✅ Project data persisted to cloud:', result.projectId);
        } else {
          throw new Error(`API returned ${response.status}`);
        }
      } catch (apiError) {
        log('⚠️ Cloud persistence failed, using localStorage fallback:', apiError);
        // Continue with localStorage fallback
      }

      // NEW: Store in pending queue (for auto-save timing issue)
      // This ensures we don't lose variant data if PitchPrint hasn't written to localStorage yet
      const pendingKey = `pp_variant_pending_${projectId}`;
      const pendingData = {
        variantId: currentVariantData.id,
        options: currentVariantData.options,
        numPages: numPages,
        timestamp: Date.now(),
        autoSaved: autoSaved
      };

      try {
        localStorage.setItem(pendingKey, JSON.stringify(pendingData));
        log('📝 Stored in pending queue:', pendingData);
      } catch (e) {
        log('⚠️ Failed to store pending data:', e);
      }

      // Save variant state with project association (legacy localStorage)
      saveVariantState(currentVariantData.id, currentVariantData.options, projectId);
      log('💾 Variant state saved with project:', { projectId, variant: currentVariantData });

      // Lock controls
      lockControls();

      // Notify StateManager with COMPLETE project metadata
      if (stateManager) {
        stateManager.setProject({
          projectId: projectId,
          variant: currentVariantData,
          numPages: eventNumPages,
          shape: shape,
          sides: sides,
          size: size
        });

        log('📡 StateManager notified with complete project data (project-saved):', {
          projectId,
          variantId: currentVariantData.id,
          numPages: eventNumPages,
          shape,
          sides
        });
      }

    } catch (error) {
      log('❌ Error handling project saved:', error);
    }
  };

  const handleProjectCleared = () => {
    log('🧹 Project cleared - unlocking variants');
    
    try {
      clearSavedVariantState();
      unlockControls();
      
      if (stateManager) {
        stateManager.clearProject();
      }
    } catch (error) {
      log('❌ Error handling project cleared:', error);
    }
  };

  // =====================================
  // UTILITY FUNCTIONS - All Safe
  // =====================================

  const getCurrentVariantData = () => {
    try {
      const variantId = document.querySelector('input[name="id"]')?.value;
      const options = Array.from(document.querySelectorAll('select[name^="options"]'))
        .map(select => select.value)
        .filter(value => value && value !== '');
      
      return { id: variantId, options };
    } catch (error) {
      log('❌ Error getting current variant data:', error);
      return { id: null, options: [] };
    }
  };

  const getActiveProjectId = () => {
    try {
      // DEBUG: Inspect localStorage structure
      log('🔍 INSPECTING LOCALSTORAGE FOR PROJECT:');
      log('  pprint-projects:', localStorage.getItem('pprint-projects')?.substring(0, 300));
      log('  StateManager projectId:', stateManager?.getProjectId());
      log('  ppclient._vars.projectId:', window.ppclient?._vars?.projectId);

      // Check StateManager first
      if (stateManager && stateManager.getProjectId()) {
        log('✅ Found projectId in StateManager');
        return stateManager.getProjectId();
      }

      // Check PitchPrint client
      if (window.ppclient?._vars?.projectId) {
        log('✅ Found projectId in ppclient._vars');
        return window.ppclient._vars.projectId;
      }

      log('❌ No projectId found in StateManager or ppclient');
      return null;
    } catch (error) {
      log('❌ Error getting active project ID:', error);
      return null;
    }
  };

  // NEW: Get project data with variant information from PitchPrint localStorage
  const getProjectDataFromPitchPrintStorage = (projectId) => {
    try {
      if (!projectId) return null;

      log('🔍 Looking for project data in PitchPrint localStorage:', projectId);

      // Check all three PitchPrint localStorage keys
      const pprintProjects = localStorage.getItem('pprint-projects');
      const pprintSp = localStorage.getItem('pprint-sp');

      if (pprintProjects) {
        const projects = JSON.parse(pprintProjects);
        const encodedData = projects[projectId];

        if (encodedData) {
          try {
            // Decode URL-encoded JSON
            const projectData = JSON.parse(decodeURIComponent(encodedData));
            log('✅ Found project data in pprint-projects:', projectData);

            // Return variant data if present
            if (projectData.shopifyVariant) {
              log('✅ Found shopifyVariant in project data:', projectData.shopifyVariant);
              return projectData.shopifyVariant;
            }
          } catch (e) {
            log('⚠️ Error decoding project data:', e);
          }
        }
      }

      // Fallback: check pprint-sp by product ID
      if (pprintSp) {
        const productId = getProductId();
        if (productId) {
          const projects = JSON.parse(pprintSp);
          const encodedData = projects[productId];

          if (encodedData) {
            try {
              // Handle both URL-encoded strings and already-parsed objects
              let projectData;
              if (typeof encodedData === 'string') {
                projectData = JSON.parse(decodeURIComponent(encodedData));
              } else if (typeof encodedData === 'object') {
                projectData = encodedData;
              } else {
                log('⚠️ Unexpected data type in pprint-sp:', typeof encodedData);
                return null;
              }

              if (projectData.projectId === projectId && projectData.shopifyVariant) {
                log('✅ Found shopifyVariant in pprint-sp:', projectData.shopifyVariant);
                return projectData.shopifyVariant;
              }
            } catch (e) {
              log('⚠️ Error decoding pprint-sp data:', e);
            }
          }
        }
      }

      log('ℹ️ No variant data found in PitchPrint localStorage');
      return null;
    } catch (error) {
      log('❌ Error getting project data from PitchPrint storage:', error);
      return null;
    }
  };

  const saveVariantState = (variantId, options, projectId) => {
    if (!projectId || !variantId) return;

    try {
      const productId = getProductId();
      if (!productId) return;

      // Save to legacy localStorage (backwards compatibility)
      const storageKey = `pp_variant_${productId}`;
      const variantState = {
        variantId,
        options,
        projectId,
        timestamp: Date.now()
      };

      localStorage.setItem(storageKey, JSON.stringify(variantState));
      log('💾 Variant state saved to localStorage:', variantState);

      // NEW: Save to PitchPrint storage with retry logic for auto-save timing
      saveVariantToPitchPrintStorageWithRetry(variantId, options, projectId, productId);
    } catch (e) {
      log('❌ Failed to save variant state:', e);
    }
  };

  // NEW: Retry wrapper for auto-save timing issue
  const saveVariantToPitchPrintStorageWithRetry = (variantId, options, projectId, productId, attemptNumber = 1) => {
    const maxAttempts = 4;
    const delays = [0, 500, 1500, 3500]; // Cumulative delays: 0ms, 500ms, 2s, 5s

    log(`🔄 Attempt ${attemptNumber}/${maxAttempts} to save variant to PitchPrint storage`);

    const success = saveVariantToPitchPrintStorage(variantId, options, projectId, productId);

    if (!success && attemptNumber < maxAttempts) {
      const nextDelay = delays[attemptNumber] - delays[attemptNumber - 1];
      log(`⏳ Retry scheduled in ${nextDelay}ms (attempt ${attemptNumber + 1}/${maxAttempts})`);

      setTimeout(() => {
        saveVariantToPitchPrintStorageWithRetry(variantId, options, projectId, productId, attemptNumber + 1);
      }, nextDelay);
    } else if (!success && attemptNumber >= maxAttempts) {
      log('❌ All retry attempts exhausted - variant not saved to PitchPrint storage');
      log('💡 Variant is saved in legacy localStorage and pending queue');
    }
  };

  // NEW: Save variant data to PitchPrint's localStorage (cross-device persistence)
  // Returns true if successful, false if project not found (needs retry)
  const saveVariantToPitchPrintStorage = (variantId, options, projectId, productId) => {
    try {
      log('💾 Saving variant to PitchPrint storage:', { variantId, options, projectId });

      // Get existing PitchPrint localStorage
      const pprintProjects = localStorage.getItem('pprint-projects');
      const pprintSp = localStorage.getItem('pprint-sp');
      const ppW2pProjects = localStorage.getItem('pp_w2p_projects');

      if (!pprintProjects && !pprintSp) {
        log('⚠️ No PitchPrint localStorage found - will retry');
        return false;
      }

      // Check pending queue for this project (in case we're retrying)
      const pendingKey = `pp_variant_pending_${projectId}`;
      const pendingData = localStorage.getItem(pendingKey);
      if (pendingData) {
        try {
          const pending = JSON.parse(pendingData);
          log('📥 Found pending data for project:', pending);
          // Use pending data if provided values are missing
          if (!variantId && pending.variantId) variantId = pending.variantId;
          if (!options && pending.options) options = pending.options;
        } catch (e) {
          log('⚠️ Error parsing pending data:', e);
        }
      }

      // Parse existing data
      let projects = pprintProjects ? JSON.parse(pprintProjects) : {};
      let sp = pprintSp ? JSON.parse(pprintSp) : {};
      let w2p = ppW2pProjects ? JSON.parse(ppW2pProjects) : {};

      let updated = false;

      // Update pprint-projects (keyed by projectId)
      if (projects[projectId]) {
        try {
          const projectData = JSON.parse(decodeURIComponent(projects[projectId]));
          projectData.shopifyVariant = { variantId, options };
          projects[projectId] = encodeURIComponent(JSON.stringify(projectData));
          updated = true;
          log('✅ Updated pprint-projects with variant data');
        } catch (e) {
          log('⚠️ Error updating pprint-projects:', e);
        }
      }

      // Update pprint-sp (keyed by productId)
      if (sp[productId]) {
        try {
          const projectData = JSON.parse(decodeURIComponent(sp[productId]));
          if (projectData.projectId === projectId) {
            projectData.shopifyVariant = { variantId, options };
            sp[productId] = encodeURIComponent(JSON.stringify(projectData));
            updated = true;
            log('✅ Updated pprint-sp with variant data');
          }
        } catch (e) {
          log('⚠️ Error updating pprint-sp:', e);
        }
      }

      // Update pp_w2p_projects (keyed by productId)
      if (w2p[productId]) {
        try {
          const projectData = JSON.parse(decodeURIComponent(w2p[productId]));
          if (projectData.projectId === projectId) {
            projectData.shopifyVariant = { variantId, options };
            w2p[productId] = encodeURIComponent(JSON.stringify(projectData));
            updated = true;
            log('✅ Updated pp_w2p_projects with variant data');
          }
        } catch (e) {
          log('⚠️ Error updating pp_w2p_projects:', e);
        }
      }

      // Save back to localStorage if any updates were made
      if (updated) {
        if (Object.keys(projects).length > 0) {
          localStorage.setItem('pprint-projects', JSON.stringify(projects));
        }
        if (Object.keys(sp).length > 0) {
          localStorage.setItem('pprint-sp', JSON.stringify(sp));
        }
        if (Object.keys(w2p).length > 0) {
          localStorage.setItem('pp_w2p_projects', JSON.stringify(w2p));
        }
        log('✅ Variant data persisted to PitchPrint storage for cross-device access');

        // Clean up pending queue on success
        const pendingKey = `pp_variant_pending_${projectId}`;
        try {
          localStorage.removeItem(pendingKey);
          log('🧹 Cleaned up pending queue');
        } catch (e) {
          log('⚠️ Error cleaning pending queue:', e);
        }

        return true; // Success
      } else {
        log('⚠️ Project not found in PitchPrint storage - needs retry');
        return false; // Not found, needs retry
      }
    } catch (error) {
      log('❌ Error saving variant to PitchPrint storage:', error);
      return false; // Error, needs retry
    }
  };

  const getSavedVariantData = () => {
    try {
      const productId = getProductId();
      if (!productId) return null;

      const storageKey = `pp_variant_${productId}`;
      const saved = localStorage.getItem(storageKey);
      
      if (saved) {
        try {
          const data = JSON.parse(saved);
          
          // Only return if saved within last 24 hours AND has project ID
          if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000 && data.projectId) {
            return data;
          } else {
            clearSavedVariantState();
            return null;
          }
        } catch (e) {
          clearSavedVariantState();
          return null;
        }
      }
      
      return null;
    } catch (error) {
      log('❌ Error getting saved variant data:', error);
      return null;
    }
  };

  const clearSavedVariantState = () => {
    try {
      const productId = getProductId();
      if (productId) {
        const storageKey = `pp_variant_${productId}`;
        localStorage.removeItem(storageKey);
        log('🗑️ Saved variant state cleared');
      }
    } catch (error) {
      log('❌ Error clearing variant state:', error);
    }
  };

  const getProductId = () => {
    try {
      return document.querySelector('[data-product-id]')?.dataset.productId ||
             document.querySelector('input[name="product-id"]')?.value ||
             window.location.pathname.split('/').pop().split('?')[0];
    } catch (error) {
      log('❌ Error getting product ID:', error);
      return null;
    }
  };

  const lockControls = () => {
    log('🔒 Locking variant controls');
    try {
      document.querySelectorAll('.card-p-style').forEach(el => {
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.6';
        el.style.filter = 'grayscale(50%)';
      });
      document.querySelectorAll('select[name^="options"]').forEach(el => {
        el.disabled = true;
      });
    } catch (error) {
      log('❌ Error locking controls:', error);
    }
  };

  const unlockControls = () => {
    log('🔓 Unlocking variant controls');
    try {
      document.querySelectorAll('.card-p-style').forEach(el => {
        el.style.pointerEvents = '';
        el.style.opacity = '';
        el.style.filter = '';
      });
      document.querySelectorAll('select[name^="options"]').forEach(el => {
        el.disabled = false;
      });
    } catch (error) {
      log('❌ Error unlocking controls:', error);
    }
  };

  const ensurePitchPrintActiveState = (projectId) => {
    log('🔧 Ensuring PitchPrint is in active editing state for project:', projectId);
    
    try {
      if (!window.ppclient) {
        log('⚠️ PitchPrint client not available yet');
        return;
      }

      // Set PitchPrint project ID
      if (window.ppclient._vars) {
        window.ppclient._vars.projectId = projectId;
        log('✅ Set PitchPrint project ID:', projectId);
      }

      // Trigger restoration complete event
      setTimeout(() => {
        if (window.ppclient.fire) {
          const projectSavedData = {
            projectId: projectId,
            type: 'project-saved',
            source: 'restoration'
          };
          
          window.ppclient.fire('project-restoration-complete', projectSavedData);
          log('✅ Triggered PitchPrint active state setup');
        }
      }, 200);

      // Ensure UI elements are visible
      setTimeout(() => {
        const editButton = document.querySelector('#pp_edit_btn');
        const clearButton = document.querySelector('#pp_clear_design_btn');
        
        if (editButton) {
          editButton.style.display = 'inline-block';
          log('✅ Edit button activated');
        }
        
        if (clearButton) {
          clearButton.style.display = 'inline-block';
          log('✅ Clear button activated');
        }
      }, 300);

    } catch (error) {
      log('❌ Error ensuring PitchPrint active state:', error);
    }
        
        // At the very end, before the closing }, add:
      setTimeout(() => {
        forceGalleryPreviewEnable(projectId);
      }, 500);
    };

  const updatePitchPrintObservables = () => {
    try {
      if (!window.ppclient?.fire) return;
      
      const sidesEl = document.querySelector('#option1');
      const shapeEl = document.querySelector('#option2');
      
      const sidesVal = sidesEl?.value;
      const shapeVal = shapeEl?.value;

      if (sidesVal) {
        window.ppclient.fire('set-single-double-sided', sidesVal);
      }

      window.ppclient.fire('set-design-observables', {
        sidesSelector: '#option1',
        shapeSelector: '#option2',
      });
      
      log('✅ PitchPrint observables updated');
    } catch (error) {
      log('❌ Error updating PitchPrint observables:', error);
    }
  };

  // =====================================
  // CONDITIONAL VARIANTS - Show/Hide Logic
  // =====================================

  const applyConditionalVariants = () => {
    const container = document.querySelector('.custom-tab-body');
    if (!container) return;

    const conditionalEnabled = container.dataset.conditionalEnabled === 'true';
    if (!conditionalEnabled) {
      log('ℹ️ Conditional variants not enabled for this product');
      return;
    }

    try {
      const rulesJson = container.dataset.conditionalRules;
      if (!rulesJson) {
        log('⚠️ No conditional rules found');
        return;
      }

      // Parse the rules
      const rules = JSON.parse(rulesJson);
      log('📋 Conditional variant rules loaded:', rules);

      // Get current selections
      const getCurrentSelections = () => {
        const selections = {};
        document.querySelectorAll('select[name^="options"]').forEach((select, idx) => {
          const optionName = select.name.match(/\[(.*?)\]/)?.[1];
          if (optionName && select.value) {
            selections[optionName] = select.value;
            selections[`option${idx + 1}`] = select.value; // Also store by index
          }
        });
        return selections;
      };

      // Apply visibility rules
      const applyVisibilityRules = () => {
        const selections = getCurrentSelections();
        log('🔍 Current selections:', selections);

        // Reset all cards to visible first
        document.querySelectorAll('.card-p-style').forEach(card => {
          card.classList.remove('conditionally-hidden');
        });

        // Apply each rule
        rules.forEach((rule, ruleIndex) => {
          try {
            // Check if the condition is met
            let conditionMet = false;

            if (rule.if_option && rule.if_value) {
              const selectedValue = selections[rule.if_option];
              conditionMet = selectedValue === rule.if_value;
              log(`Rule ${ruleIndex + 1}: ${rule.if_option} = "${selectedValue}" ${conditionMet ? '===' : '!=='} "${rule.if_value}"`);
            }

            // If condition is met, hide the target option values
            if (conditionMet && rule.then_hide_option && rule.hide_values) {
              const hideValues = Array.isArray(rule.hide_values) ? rule.hide_values : [rule.hide_values];

              hideValues.forEach(valueToHide => {
                // Find all cards for this option with this value
                // Use CSS.escape() to safely escape special characters in attribute values
                const escapedOptionName = CSS.escape(rule.then_hide_option);
                const escapedValue = CSS.escape(valueToHide);
                const cardsToHide = document.querySelectorAll(
                  `.card-p-style[data-option-name="${escapedOptionName}"][data-value="${escapedValue}"]`
                );

                cardsToHide.forEach(card => {
                  card.classList.add('conditionally-hidden');
                  log(`🙈 Hiding: ${rule.then_hide_option} = "${valueToHide}"`);
                });
              });
            }
          } catch (ruleError) {
            log(`❌ Error applying rule ${ruleIndex + 1}:`, ruleError);
          }
        });

        // If the currently active option was hidden, select the first visible one
        document.querySelectorAll('.option-group').forEach(group => {
          const activeCard = group.querySelector('.card-p-style.active');
          if (activeCard && activeCard.classList.contains('conditionally-hidden')) {
            log('⚠️ Active option was hidden, selecting first visible option');

            const firstVisible = group.querySelector('.card-p-style:not(.conditionally-hidden)');
            if (firstVisible) {
              // Programmatically click the first visible option
              firstVisible.click();
            }
          }
        });
      };

      // Apply rules on page load
      applyVisibilityRules();

      // Watch for variant changes
      document.querySelectorAll('select[name^="options"]').forEach(select => {
        select.addEventListener('change', () => {
          log('🔄 Option changed, reapplying conditional rules');
          applyVisibilityRules();
        });
      });

      log('✅ Conditional variants system initialized');
    } catch (error) {
      log('❌ Error setting up conditional variants:', error);
    }
  };

  // =====================================
  // INITIALIZATION - Safe & Robust
  // =====================================

  const init = () => {
    if (initialized) {
      log('Already initialized, skipping...');
      return;
    }
    initialized = true;
    log('🚀 Initializing streamlined card variants system...');

    try {
      setupVariantControls();
      setupStateManagerListeners();
      setupPitchPrintIntegration();
      applyConditionalVariants(); // NEW: Initialize conditional variants

      log('✅ Streamlined card variants system ready');
    } catch (error) {
      log('❌ Error during initialization:', error);
    }
  };

  const setupStateManagerListeners = () => {
    if (!stateManager) return;
    
    try {
      stateManager.on('project:restored', (event) => {
        const { projectId, variant } = event.detail;
        log('🔄 Project restored via StateManager:', { projectId, variant });
        
        if (variant && variant.id) {
          applyVariant(variant.id, variant.options);
        }
        lockControls();
      });

      stateManager.on('project:cleared', () => {
        log('🧹 Project cleared via StateManager');
        handleProjectCleared();
      });

      log('📡 StateManager listeners attached');
    } catch (error) {
      log('❌ Error setting up StateManager listeners:', error);
    }
  };

  const forceGalleryPreviewEnable = (projectId) => {
  log('🖼️ Forcing gallery preview enable for project:', projectId);

  // Track if preview has loaded successfully
  let previewLoaded = false;

  // Multiple refresh attempts: try native first, then fallback to manual injection
  const refreshAttempts = [
    { delay: 200, useManualFallback: false },   // Native
    { delay: 500, useManualFallback: false },   // Native
    { delay: 1000, useManualFallback: true },   // Manual fallback
    { delay: 2000, useManualFallback: true }    // Manual fallback
  ];

  refreshAttempts.forEach((attempt, index) => {
    setTimeout(() => {
      // Skip if preview already loaded
      if (previewLoaded) {
        log(`⏭️ Skipping attempt ${index + 1} - preview already loaded`);
        return;
      }

      const strategy = attempt.useManualFallback ? 'manual fallback' : 'native';
      log(`🔄 Gallery refresh attempt ${index + 1}/${refreshAttempts.length} (delay: ${attempt.delay}ms, strategy: ${strategy})`);

      // Method 1: Call refresh function with appropriate strategy
      if (window.refreshGalleryPreview) {
        window.refreshGalleryPreview(attempt.useManualFallback);
        log(`✅ Called refreshGalleryPreview (${strategy})`);
      } else if (window.enableGalleryPreview) {
        window.enableGalleryPreview(true, attempt.useManualFallback);
        log(`✅ Called enableGalleryPreview (${strategy})`);
      }

      // Method 2: Fire PitchPrint events (only for native attempts)
      if (!attempt.useManualFallback && window.ppclient?.fire) {
        window.ppclient.fire('enable-gallery-preview', { projectId });
        window.ppclient.fire('project-gallery-restore', { projectId });
        window.ppclient.fire('render-preview');
        log('🔥 Fired gallery events (native)');
      }

      // Method 3: Show hidden gallery elements
      const selectors = ['.pitchprint-gallery-preview', '.gallery-preview', '.pitchprint-preview-image'];
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          if (el.style.display === 'none') {
            el.style.display = 'block';
            log('🖼️ Showed gallery element:', selector);
          }
        });
      });

      // Check if preview loaded successfully after this attempt
      setTimeout(() => {
        const previewImages = document.querySelectorAll('.pitchprint-preview-image');
        const visiblePreviews = Array.from(previewImages).filter(img => {
          return img.offsetParent !== null && img.src && img.src.length > 0;
        });

        if (visiblePreviews.length > 0) {
          previewLoaded = true;
          log(`✅ Preview loaded successfully on attempt ${index + 1} - skipping remaining retries`);
        }
      }, 100);
    }, attempt.delay);
  });
};


  const setupPitchPrintIntegration = () => {
    // Check if this is a PitchPrint product using window.productConfig
    const isPitchPrintProduct = window.productConfig?.usesPitchprint === true;

    if (!isPitchPrintProduct) {
      // For non-PitchPrint products, immediately apply popular variants and unlock controls
      log('ℹ️ Non-PitchPrint product - skipping PitchPrint integration');
      setTimeout(() => {
        applyPopularVariants();
        unlockControls();
        log('✅ Standard product variant controls ready');
      }, 100);
      return;
    }

    // PitchPrint product - wait for PitchPrint client
    let attempts = 0;
    const maxAttempts = 50;

    const watcher = setInterval(() => {
      attempts++;

      try {
        if (window.ppclient && window.ppclient.on) {
          clearInterval(watcher);
          log('🎨 PitchPrint client detected');

          window.ppclient.on('project-saved', handleProjectSaved);
          log('✅ PitchPrint project-saved handler attached');

          // Initial project check
          setTimeout(() => {
            handleProjectRestoration();
          }, 500);

          if (stateManager) {
            stateManager.setSystemReady();
          }
          return;
        }

        if (attempts % 10 === 0) {
          log(`⏳ Waiting for PitchPrint... (${attempts}/${maxAttempts})`);
        }

        if (attempts >= maxAttempts) {
          clearInterval(watcher);
          log('⚠️ PitchPrint timeout - proceeding without integration');

          setTimeout(() => {
            handleProjectRestoration();
          }, 500);

          if (stateManager) {
            stateManager.setSystemReady();
          }
        }
      } catch (error) {
        log('❌ Error in PitchPrint integration setup:', error);
        if (attempts >= maxAttempts) {
          clearInterval(watcher);
        }
      }
    }, 200);
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }

  log('📝 Streamlined card variants script loaded');
})();